import type { Personnel } from "@/lib/brokerAccounts";
import { parseDecimal } from "@/lib/accountWrite";
import { OFFICER_TITLE_LABELS } from "@/lib/officerTitles";
import type { PersonnelRole } from "@/lib/personnelRoles";
import { toPhoneInputs, toPhoneValues, type PhoneInput } from "@/lib/phone";
import type {
  SubmissionPerson,
  SubmissionPersonInput,
  WcInclusion,
} from "@/lib/submissions/brokerSubmissions";

// The Workers' Comp wizard's People step (BRK-S303), as pure rules. Owners and
// officers are the submission's own people (PMS-S740), never the account's: an
// existing account's people change only by endorsement (Nav, 2026-10-08), so
// the step copies them in or adds new ones, and the account gets them at bind.
//
// The submission's people are one list with roles, contacts included (BRK-S337),
// and PMS replaces it whole (`peopleStep.ts` writes it). Since BRK-S391 an
// owner's contact roles are ticked on their own row, and the title is the
// legal entity's (PMS-S813), sent as text.

/** The roles that make a person one of the People step's. */
export const OFFICER_ROLES: readonly PersonnelRole[] = [
  "owner",
  "executive_officer",
];

/** ACORD 130's contact types, which PMS keeps as person roles (PMS-S178). */
export const CONTACT_ROLES = ["inspection", "accounting", "claims"] as const;
export type ContactRole = (typeof CONTACT_ROLES)[number];

export function isContactRole(role: PersonnelRole): role is ContactRole {
  return CONTACT_ROLES.some((c) => c === role);
}

export const OWNERSHIP_OVER_100 =
  "Ownership shares can’t add up to more than 100%.";

/** One owner or officer as the step edits them. */
/** Whether the policy covers them: the step's choices, and Review's labels. */
export const INCLUSION_OPTIONS: { value: WcInclusion; label: string }[] = [
  { value: "included", label: "Included" },
  { value: "excluded", label: "Excluded" },
];

export interface WcPerson {
  /** The row's own key: the saved person's id, or a new row's client key. */
  key: string;
  /** The submission person's id, once PMS holds them. */
  id?: string;
  /** The account person they were copied from. */
  sourcePersonnelId?: string;
  firstName: string;
  lastName: string;
  /** Their title, as typed or chosen from the legal entity's (BRK-S391). */
  title: string;
  /** The submission location they work at, one with payroll; "" until
   *  chosen (BRK-S391). */
  locationId: string;
  /** What else they're the contact for, ticked on their row (BRK-S391). */
  contactRoles: ContactRole[];
  ownershipPercentage: number | undefined;
  wcInclusion: WcInclusion | "";
  /** Annual payroll, a plain number (D-10). Kept when switched to excluded. */
  annualRemuneration: number | undefined;
  /** What else the person holds, carried through the write untouched; their
   *  contact roles are `contactRoles`'. */
  carried: {
    email?: string;
    phones: PhoneInput[];
    roles: PersonnelRole[];
  };
}

/** A new row; `locationId` is the only location, when there is one. */
export function newPerson(key: string, locationId = ""): WcPerson {
  return {
    key,
    firstName: "",
    lastName: "",
    title: "",
    locationId,
    contactRoles: [],
    ownershipPercentage: undefined,
    wcInclusion: "",
    annualRemuneration: undefined,
    carried: { phones: [], roles: [] },
  };
}

export function isOwnerOrOfficer(p: {
  roles: readonly PersonnelRole[];
}): boolean {
  return p.roles.some((r) => OFFICER_ROLES.includes(r));
}

function decimal(value: string | null | undefined): number | undefined {
  return parseDecimal(value ?? null) ?? undefined;
}

/** Someone on the submission as a row: the person's saved facts, and
 *  everything else about them carried. A contact taken into the step gains
 *  the officer role rather than being entered twice (BRK-S337). */
export function personFromSubmission(
  p: SubmissionPerson,
  key: string,
): WcPerson {
  return {
    key,
    id: p.id,
    sourcePersonnelId: p.sourcePersonnelId ?? undefined,
    firstName: p.firstName,
    lastName: p.lastName,
    // An owner saved before BRK-S391 has only Pie's officer title: its label
    // starts their title, so it carries over, as text.
    title:
      p.title ?? (p.officerTitle ? OFFICER_TITLE_LABELS[p.officerTitle] : ""),
    // A location that lost its payroll reads as unchosen, so it's asked again.
    locationId: p.locationValid === false ? "" : (p.locationId ?? ""),
    contactRoles: p.roles.filter(isContactRole),
    ownershipPercentage: decimal(p.ownershipPercentage),
    wcInclusion: p.wcInclusion ?? "",
    annualRemuneration: decimal(p.annualRemuneration),
    carried: {
      email: p.email ?? undefined,
      phones: toPhoneInputs(toPhoneValues(p.phones)),
      roles: p.roles,
    },
  };
}

/** The step's rows: the submission's owners and officers, in PMS's order. */
export function peopleFrom(personnel: readonly SubmissionPerson[]): WcPerson[] {
  return personnel
    .filter(isOwnerOrOfficer)
    .map((p) => personFromSubmission(p, p.id));
}

/** An account person copied in as a new row, with their title and contact
 *  roles as the account holds them; the location is the only one, if any. */
export function personFromAccount(
  p: Personnel,
  key: string,
  locationId = "",
): WcPerson {
  return {
    key,
    sourcePersonnelId: p.id,
    firstName: p.firstName,
    lastName: p.lastName,
    title: p.title ?? "",
    locationId,
    contactRoles: p.roles.filter(isContactRole),
    ownershipPercentage: decimal(p.ownershipPercentage),
    wcInclusion: p.wcInclusion ?? "",
    annualRemuneration: decimal(p.annualRemuneration),
    carried: {
      email: p.email ?? undefined,
      phones: toPhoneInputs(toPhoneValues(p.phones)),
      roles: p.roles,
    },
  };
}

/** Who can be added: the submission's other people (its contacts), then the
 *  account's people who aren't on the submission yet, saved or not. */
export function peopleCandidates(
  accountPeople: readonly Personnel[],
  personnel: readonly SubmissionPerson[],
  rows: readonly WcPerson[],
): { onSubmission: SubmissionPerson[]; onAccount: Personnel[] } {
  const shown = new Set(rows.map((r) => r.id));
  const taken = new Set<string>();
  for (const p of personnel)
    if (p.sourcePersonnelId) taken.add(p.sourcePersonnelId);
  for (const r of rows) if (r.sourcePersonnelId) taken.add(r.sourcePersonnelId);
  return {
    onSubmission: personnel.filter(
      (p) => !isOwnerOrOfficer(p) && !shown.has(p.id),
    ),
    onAccount: accountPeople.filter((p) => !taken.has(p.id)),
  };
}

/** A person as PMS holds them, in write shape: what a step carries back for
 *  the people it doesn't show (contacts reuse it, BRK-S337). `asOfficer`
 *  false drops the facts that make them an owner or officer. */
export function personAsInput(
  p: SubmissionPerson,
  asOfficer = true,
): SubmissionPersonInput {
  const ownership = asOfficer ? decimal(p.ownershipPercentage) : undefined;
  const payroll = asOfficer ? decimal(p.annualRemuneration) : undefined;
  const inclusion = asOfficer ? p.wcInclusion : null;
  return {
    id: p.id,
    firstName: p.firstName,
    lastName: p.lastName,
    ...(p.title ? { title: p.title } : {}),
    ...(p.officerTitle ? { officerTitle: p.officerTitle } : {}),
    roles: p.roles,
    ...(p.email ? { email: p.email } : {}),
    phones: toPhoneInputs(toPhoneValues(p.phones)),
    ...(ownership !== undefined ? { ownershipPercentage: ownership } : {}),
    ...(inclusion ? { wcInclusion: inclusion } : {}),
    ...(payroll !== undefined ? { annualRemuneration: payroll } : {}),
    ...(p.sourcePersonnelId ? { sourcePersonnelId: p.sourcePersonnelId } : {}),
  };
}

/** An owner or officer in write shape (BRK-S391): their title as text, never
 *  an officer title (an absent one keeps what PMS holds), their location, and
 *  the contact roles ticked on their row. A person the step adds gets the
 *  owner role with a share, else the officer role. */
export function ownerInput(r: WcPerson): SubmissionPersonInput {
  const added: PersonnelRole =
    (r.ownershipPercentage ?? 0) > 0 ? "owner" : "executive_officer";
  const held = r.carried.roles.filter((role) => !isContactRole(role));
  const roles = isOwnerOrOfficer({ roles: held }) ? held : [...held, added];
  const title = r.title.trim();
  return {
    ...(r.id ? { id: r.id } : {}),
    firstName: r.firstName.trim(),
    lastName: r.lastName.trim(),
    ...(title ? { title } : {}),
    roles: [...roles, ...r.contactRoles],
    ...(r.carried.email ? { email: r.carried.email } : {}),
    phones: r.carried.phones,
    ...(r.ownershipPercentage !== undefined
      ? { ownershipPercentage: r.ownershipPercentage }
      : {}),
    ...(r.wcInclusion ? { wcInclusion: r.wcInclusion } : {}),
    ...(r.annualRemuneration !== undefined
      ? { annualRemuneration: r.annualRemuneration }
      : {}),
    ...(r.locationId ? { locationId: r.locationId } : {}),
    ...(r.sourcePersonnelId ? { sourcePersonnelId: r.sourcePersonnelId } : {}),
  };
}

/** The bag's `includeOfficers`: whether anyone is covered by the policy. */
export function includeOfficers(rows: readonly WcPerson[]): boolean {
  return rows.some((r) => r.wcInclusion === "included");
}
